/**
 * `v-reveal` — a section fades and rises the first time it scrolls into view.
 *
 * The cheapest thing that makes a long page feel built rather than dumped. A
 * static page that is entirely present on first paint reads as a mockup; content
 * that arrives as you reach it reads as a product.
 *
 * ONE OBSERVER FOR THE WHOLE PAGE, not one per element. A lobby has a dozen
 * revealing sections and a dozen IntersectionObservers is a dozen callbacks on
 * every scroll frame — on the mid-range Android this site is mostly viewed on,
 * that is the difference between smooth and not.
 *
 * Unobserved once revealed. This is an entrance, not a scroll-linked effect, and
 * an element that faded away again when scrolled past would be maddening.
 */
let observer;

const REVEALED = "is-revealed";

function ensureObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        entry.target.classList.add(REVEALED);
        observer.unobserve(entry.target);
      }
    },
    {
      // Fires slightly before the element reaches the viewport, so the
      // animation is already finishing by the time it is properly in view.
      // Revealing exactly on the edge means the user watches it happen, which
      // draws attention to the effect rather than to the content.
      rootMargin: "0px 0px -12% 0px",
      threshold: 0.05,
    },
  );

  return observer;
}

export const reveal = {
  mounted(el, binding) {
    // Honour the OS setting, and also skip the whole mechanism when the browser
    // has no IntersectionObserver — the fallback is simply "already visible",
    // which is the correct degraded state.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (reduced || typeof IntersectionObserver === "undefined") {
      el.classList.add(REVEALED);
      return;
    }

    el.classList.add("reveal");

    // A stagger, when asked for. `v-reveal="2"` delays by two steps, so a row of
    // cards can arrive in sequence rather than as one block.
    if (binding.value) {
      el.style.transitionDelay = `${Math.min(Number(binding.value), 6) * 70}ms`;
    }

    ensureObserver().observe(el);
  },

  unmounted(el) {
    observer?.unobserve(el);
  },
};
